/**
 * 저작 job 의 프런트 표현 — 백엔드 `GET /masterplan/{thread}/job` 응답 그대로.
 *
 * ⚠ `pct_est` 는 **추정**이고 응답이 `estimated: true` 로 그 사실을 스스로 말한다(B §9).
 * 화면은 그 표기를 지우면 안 된다 — 없는 정밀도를 만들어 보이면 8분짜리 작업에서 사용자가
 * 남은 시간을 계산하고, 그 계산이 틀린다.
 */
export type JobStatus = 'queued' | 'running' | 'complete' | 'failed' | 'cancelled'

/** §9 사전 등록 단계 어휘. 백엔드 `JobPhase` 와 같다. */
export type JobPhase = 'queued' | 'corpus' | 'authoring' | 'repair' | 'render' | 'save'

export interface JobView {
  job_id: string
  thread_id: string
  status: JobStatus
  phase: JobPhase
  pct_est: number
  estimated: boolean
  queue_position: number | null
  note: string
  error: string
  claims: number
  created_at: string | null
  finished_at: string | null
}

export interface ThreadJobResponse {
  thread_id: string
  job: JobView | null
  /**
   * 저작이 남긴 **비차단 경고**(W1). 값 자체를 막지는 않지만 사람이 봐야 하는 것들 —
   * 명부에 없는 담당자, 공표단가에 없는 일단가, 남은 품질 위반.
   * 서버가 분기와 무관하게 항상 배열로 준다(빈 배열 = 검토할 것 없음).
   */
  warnings: string[]
  detail?: string
}

export interface JobListResponse {
  jobs: JobView[]
  detail?: string
}

export const ACTIVE_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>(['queued', 'running'])

export function isActive(job: JobView | null | undefined): boolean {
  return Boolean(job && ACTIVE_STATUSES.has(job.status))
}

/**
 * 단계·상태의 **i18n 키**. 식별자를 화면에 그대로 보여주지 않되(도구 라벨과 같은 이유),
 * 표시 문자열은 여기 두지 않는다 — 언어 전환기가 있는 이상 상수 파일에 한국어를 박으면
 * en/ja 에서 이 부분만 한국어로 남는다(구 앱이 그랬다).
 */
export const phaseKey = (phase: JobPhase): string => `phase.${phase}`
export const statusKey = (status: JobStatus): string => `status.${status}`
